// The game's UI pieces on the phone, drawn from the game's own sheets at
// ART_SCALE: a keyboard key (keycap.gd) and a carved wood plank
// (wood_button.gd). The device they sit in is device.ts.

import { button, buzz } from './dom';
import { drawText, type FontName, inkHeight, loadFont, widthOf } from './pixelText';
import { play } from './sfx';
import { ART_SCALE, loadSprite, nineSlice, spriteCanvas } from './sprites';

// ---- A key ------------------------------------------------------------------

// keycap-blank.png: a light key then a dark one, 16x16 each. The legend
// follows the face: near-black on the light key, light grey on the dark one
// (keycap.gd, INK_BY_FACE).
export type Face = 'light' | 'dark';
const KEYCAP_REGION: Record<Face, { x: number; y: number; w: number; h: number }> = {
  light: { x: 0, y: 0, w: 16, h: 16 },
  dark: { x: 16, y: 0, w: 16, h: 16 },
};
const KEYCAP_SLICE = { left: 2, top: 2, right: 2, bottom: 3 };
export const KEY_INK: Record<Face, string> = { light: '#0a061e', dark: '#908ca4' };
// keycap-symbols.png: 12x12 cells.
const SYMBOL_SIZE = 12;
// The dark key's art has no top rim: in the game it sits on a lighter panel.
// On the night glass its top would vanish, so it gets the rim its sides have.
const DARK_KEY_RIM = '#2a263e';
const DARK_KEY_RIM_Y = 1;
const DARK_KEY_RIM_INSET = 2;
const KEY_PRESS_PX = 1;
const KEY_BUZZ_MS = 8;

export interface KeySpec {
  // What a screen reader says, and the legend when nothing else is given.
  label: string;
  // A cell of keycap-symbols.png in place of a word.
  symbol?: number | undefined;
  // Art or words laid on the face in place of a drawn legend.
  content?: HTMLElement | undefined;
  font?: FontName | undefined;
  face?: Face | undefined;
  className?: string | undefined;
  onPress: () => void;
}

const repaints = new WeakMap<HTMLButtonElement, () => void>();
// Keys held down by the page, not a finger: a choice that stays chosen.
const heldKeys = new WeakSet<HTMLButtonElement>();

// A keyboard key, sized by its CSS box: the game's keycap nine-sliced to fit,
// sunk a pixel while it is held. It fires on the press, not the release, so a
// fast thumb never waits on it. A disabled key draws dark and ignores presses.
export function keycap(spec: KeySpec): HTMLButtonElement {
  const key = button(`keycap ${spec.className ?? ''}`.trim(), '', () => {});
  key.setAttribute('aria-label', spec.label);
  const canvas = document.createElement('canvas');
  canvas.setAttribute('aria-hidden', 'true');
  key.append(canvas);
  if (spec.content) {
    const face = document.createElement('span');
    face.className = 'keycap-face';
    face.append(spec.content);
    key.append(face);
  }
  let down = false;
  const paint = (): void => {
    const w = Math.floor(key.clientWidth / ART_SCALE);
    const h = Math.floor(key.clientHeight / ART_SCALE);
    if (w <= 0 || h <= 0) return;
    const face: Face = key.disabled ? 'dark' : (spec.face ?? 'light');
    void Promise.all([
      loadSprite('/ui/keycap-blank.png'),
      loadSprite('/ui/keycap-symbols.png'),
      loadFont(spec.font ?? 'buzzer'),
    ]).then(([blank, symbols, font]) => {
      const context = spriteCanvas(canvas, w, h, ART_SCALE);
      const sink = down || heldKeys.has(key) ? KEY_PRESS_PX : 0;
      nineSlice(context, blank, KEYCAP_REGION[face], KEYCAP_SLICE, {
        x: 0,
        y: sink,
        w,
        h: h - sink,
      });
      if (face === 'dark') {
        context.fillStyle = DARK_KEY_RIM;
        context.fillRect(DARK_KEY_RIM_INSET, DARK_KEY_RIM_Y + sink, w - DARK_KEY_RIM_INSET * 2, 1);
      }
      if (spec.content) return;
      const middle = Math.floor((h - KEYCAP_SLICE.bottom) / 2) + sink;
      if (spec.symbol !== undefined) {
        const x = Math.floor((w - SYMBOL_SIZE) / 2);
        const sx = spec.symbol * SYMBOL_SIZE;
        const y = middle - SYMBOL_SIZE / 2;
        context.drawImage(symbols, sx, 0, SYMBOL_SIZE, SYMBOL_SIZE, x, y, SYMBOL_SIZE, SYMBOL_SIZE);
        return;
      }
      const x = Math.floor((w - widthOf(font, spec.label)) / 2);
      drawText(
        context,
        font,
        spec.label,
        x,
        middle - Math.floor(inkHeight(font) / 2),
        KEY_INK[face],
      );
    });
  };
  const press = (on: boolean): void => {
    down = on;
    key.classList.toggle('is-down', on);
    paint();
  };
  repaints.set(key, paint);
  new ResizeObserver(paint).observe(key);
  key.addEventListener('pointerdown', (event) => {
    if (key.disabled) return;
    event.preventDefault();
    press(true);
    play('click', 1.15);
    buzz(KEY_BUZZ_MS);
    spec.onPress();
  });
  for (const end of ['pointerup', 'pointercancel', 'pointerleave'] as const) {
    key.addEventListener(end, () => {
      if (down) press(false);
    });
  }
  // A keyboard or switch user presses with Enter or Space, which is a click.
  key.addEventListener('click', (event) => {
    if (event.detail === 0 && !key.disabled) spec.onPress();
  });
  return key;
}

// Enables or disables a key and redraws its face to match.
export function setKeyEnabled(key: HTMLButtonElement, on: boolean): void {
  if (key.disabled === !on) return;
  key.disabled = !on;
  repaints.get(key)?.();
}

// Holds a key down, or lets it up, and redraws it. A held key shows a choice
// that stays chosen, such as this llama's mood vote through its cooldown.
export function setKeyHeld(key: HTMLButtonElement, held: boolean): void {
  if (heldKeys.has(key) === held) return;
  if (held) heldKeys.add(key);
  else heldKeys.delete(key);
  key.classList.toggle('is-held', held);
  repaints.get(key)?.();
}

// ---- A plank ----------------------------------------------------------------

// wood-buttons.png: three columns (rest, hover, pressed) of a big and a small
// plank. The caps are 56 wide; the middle tiles (wood_button.gd).
const PLANK_REST_X = 0;
const PLANK_DOWN_X = 352;
const PLANK_SMALL_Y = 64;
const PLANK_W = 160;
export const PLANK_H = 32;
const PLANK_SLICE = { left: 56, top: 0, right: 56, bottom: 0 };
const PLANK_MIN_W = 112;
// The carving: three passes of the word, back to front (wood_button.gd).
const CARVE: [string, number][] = [
  ['#c09473', 1],
  ['#ad7757', -1],
  ['#0a061e', 0],
];
const CARVE_Y_OFFSET = -3;
const PLANK_PRESS_TEXT_PX = 2;

// A wood plank with the word carved in it, lowercase as the game carves it. It
// is as wide as its CSS box, never under the plank's two caps.
export function plank(label: string, onPress: () => void): HTMLButtonElement {
  const node = button('plank', '', () => {});
  node.setAttribute('aria-label', label);
  const canvas = document.createElement('canvas');
  canvas.setAttribute('aria-hidden', 'true');
  node.append(canvas);
  const word = label.toLowerCase();
  let down = false;
  const paint = (): void => {
    const w = Math.max(PLANK_MIN_W, Math.floor(node.clientWidth / ART_SCALE));
    void Promise.all([loadSprite('/ui/wood-buttons.png'), loadFont('troll')]).then(
      ([sheet, font]) => {
        const context = spriteCanvas(canvas, w, PLANK_H, ART_SCALE);
        const region = {
          x: down ? PLANK_DOWN_X : PLANK_REST_X,
          y: PLANK_SMALL_Y,
          w: PLANK_W,
          h: PLANK_H,
        };
        nineSlice(context, sheet, region, PLANK_SLICE, { x: 0, y: 0, w, h: PLANK_H });
        const x = Math.floor((w - widthOf(font, word)) / 2);
        const y =
          Math.floor((PLANK_H - inkHeight(font)) / 2) +
          CARVE_Y_OFFSET +
          (down ? PLANK_PRESS_TEXT_PX : 0);
        for (const [color, lift] of CARVE) drawText(context, font, word, x, y + lift, color);
      },
    );
  };
  const press = (on: boolean): void => {
    down = on;
    paint();
  };
  new ResizeObserver(paint).observe(node);
  node.addEventListener('pointerdown', () => press(true));
  for (const end of ['pointerup', 'pointercancel', 'pointerleave'] as const) {
    node.addEventListener(end, () => {
      if (down) press(false);
    });
  }
  node.addEventListener('click', onPress);
  return node;
}
